import { createApi } from '@reduxjs/toolkit/query/react'
import { BaseSessionRequest } from '../media/media'
import { BaseRequest } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { AiExecuteResult } from '../../models/AiResult'
import { AIAgentType } from '../../models/AIAgent'

const _BASE_URL_EXECUTE = import.meta.env.REACT_APP_AI_EXECUTE_API_BASE_URL!

export interface AIExecuteSessionPayload {
  aiFeatureIds?: string[]
  userId?: string
  saveResults?: boolean
  meetingTemplateId?: string
  context?: object
}

export interface AIExecuteSystemFeaturePayload {
  systemPresetName: string
  context?: { [key: string]: any }
}

export interface AIExecuteSystemFeatureForSessionPayload {
  systemPresetName: string
  context?: { [key: string]: any }
}

export interface ExecuteSystemFeatureRequest extends BaseRequest {
  payload: AIExecuteSystemFeaturePayload
}

export interface ExecuteSystemFeatureForSessionRequest extends BaseSessionRequest {
  payload: AIExecuteSystemFeatureForSessionPayload
}

export interface ExecuteSessionRequest extends BaseSessionRequest {
  payload: AIExecuteSessionPayload
}

export interface ExecuteAskMeAnythingRequest {
  accountId: string
  userId: string
  payload: AskMeAnythingPayload
}

export interface AskMeAnythingPayload {
  question: string
}

export interface CitationExecutePayload {
  userId: string
  aiAgentResultId: string
  aiAgentType: AIAgentType
}

export interface ExecuteCitationRequest extends BaseRequest {
  payload: CitationExecutePayload
}

export interface ClientProfileInitialCRMSyncExecutePayload {
  userId: string
}

export interface ClientProfileInitialCRMSyncRequest extends BaseRequest {
  payload: ClientProfileInitialCRMSyncExecutePayload
}

export const aiExecuteApi = createApi({
  reducerPath: 'executeApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL_EXECUTE),
  endpoints: (builder) => ({
    executeSystemPreset: builder.mutation<AiExecuteResult[], ExecuteSystemFeatureRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/ai-management/execute-system-feature`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: AiExecuteResult[] }) => response.data,
    }),
    executeSystemPresetForSession: builder.mutation<AiExecuteResult[], ExecuteSystemFeatureForSessionRequest>({
      query: ({ accountId, roomId, sessionId, payload }) => ({
        url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/ai-management/execute-system-feature`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: AiExecuteResult[] }) => response.data,
    }),
    executeSession: builder.mutation<AiExecuteResult[], ExecuteSessionRequest>({
      query: ({ accountId, roomId, sessionId, payload }) => ({
        url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/ai-management/execute`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: AiExecuteResult[] }) => response.data,
    }),
    executeCitation: builder.mutation<AiExecuteResult[], ExecuteCitationRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/ai-management/execute-citation`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: AiExecuteResult[] }) => response.data,
    }),
    executeClientProfileInitialCRMSync: builder.mutation<void, ClientProfileInitialCRMSyncRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/ai-management/client-profiles/execute-initial-crm-sync`,
        method: 'POST',
        body: payload,
      }),
    }),
  }),
})

export const {
  useExecuteSystemPresetMutation,
  useExecuteSystemPresetForSessionMutation,
  useExecuteSessionMutation,
  useExecuteCitationMutation,
  useExecuteClientProfileInitialCRMSyncMutation,
} = aiExecuteApi
